// Public Run API contract (POST /v1/runs/preview, POST /v1/runs, GET /v1/runs/:id).
//
// This is the *managed run* control surface exposed by the public Ato API
// (api.ato.run) — NOT the DesktopRuntime control plane (/v1/runtime/*).
// The Store "Get & Run" flow resolves a capsule, previews how it can run
// (managed cloud vs. local desktop companion), and then creates a run.
//
// The canonical server response (#593) nests the run-preview decision under a
// `contract` object with a *typed blocker taxonomy*, an explicit `trustGate`,
// and an `estimatedMode`. Older / partially-deployed servers may instead expose
// the same advisory fields at the top level. The normalizers below prefer
// `raw.contract` and fall back to top-level fields only for compatibility — and
// never treat a missing contract / placement / estimatedMode as runnable.

import {
  sessionSurfaceFromEnvelope,
  type SessionSurface,
} from "./sessionSurface";

/** What the *client* asks for. "auto" lets the server pick the best placement;
 *  "external-runner" targets an enrolled Connected Runner (requires runnerId). */
export type RequestedPlacement = "auto" | "local" | "cloud" | "external-runner";

/** What the server actually *resolved* to. */
export type ResolvedPlacement = "cloud" | "local" | "desktop";

/** Describes the calling client so the server can decide eligible placements. */
export interface RunClientInfo {
  os: string; // "web" for the PWA
  desktop_companion_available: boolean;
}

export interface RunPreviewRequest {
  /** Internal Store ref ("publisher/slug", a ULID, capsule:// or github URL). */
  source: string;
  placement: RequestedPlacement;
  client: RunClientInfo;
}

/** Bind a one-time guest app-view token to the exact proxy URL returned by the
 * API. The token stays in memory and is never persisted with the run. */
export function withAppViewToken(url: string, token: string): string {
  const parsed = new URL(url);
  parsed.searchParams.set("app_view_token", token);
  return parsed.toString();
}

export interface CreateRunRequest {
  /** Internal Store ref. Used only when `previewId` is absent. */
  source: string;
  placement: RequestedPlacement;
  client: RunClientInfo;
  /** Preferred capsule identity: the id returned by a prior preview. */
  previewId?: string;
  /** Required when placement is "external-runner": the enrolled Connected
   *  Runner that should claim this run. */
  runnerId?: string;
}

/**
 * A reason the run cannot proceed (yet) at the selected placement.
 *
 * Known codes are listed for ergonomics, but the type stays open (`| string`)
 * so an unknown code from a newer server is still rendered honestly rather than
 * silently treated as "no blocker".
 */
export type BlockerCode =
  | "needs_desktop_companion"
  // Cloud-not-available aliases (server contract not finalized).
  | "cloud_not_implemented"
  | "cloud_unavailable"
  | "cloud_substrate_unavailable"
  // Trust/review aliases.
  | "requires_review"
  | "requires_review_for_cloud"
  | "cloud_execution_not_allowed"
  | "requires_consent"
  | "requires_budget"
  | "requires_metering_budget"
  | (string & {});

export interface RunBlocker {
  code: BlockerCode;
  message?: string;
}

export interface ConsentRequirement {
  id?: string;
  label?: string;
}

export interface BudgetGate {
  state?: string; // e.g. "requires_budget" | "ok" | "required" | "unknown"
  required_cents?: number;
  currency?: string;
  message?: string;
}

/** Lifecycle status of a created run. Open type to tolerate new states. */
export type RunStatus =
  | "queued"
  | "provisioning"
  | "starting"
  | "needs_consent"
  | "running"
  | "ready"
  | "failed"
  | "stopping"
  | "stopped"
  | "cancelled"
  | (string & {});

/**
 * Statuses after which polling should stop. `stopping` is deliberately absent:
 * it is transient (the runner is tearing the workload down) and must be polled
 * through until it settles on `stopped` (clean ack) or `failed` (the API
 * recorded an incomplete teardown). `cancelled` is terminal — a queued run
 * stopped before dispatch never runs.
 */
export const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set([
  "ready",
  "failed",
  "stopped",
  "cancelled",
]);

export function isTerminalRunStatus(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.has(status);
}

// ──────────────────────────────────────────────────────────────────────────────
// Normalized contract (what the rest of the app consumes)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Normalized trust gate. `absent` means the server returned no gate at all
 * (tolerated for compatibility); `unknown` means the server explicitly has not
 * evaluated trust. A managed cloud run is only offered for `allowed`/`absent`.
 */
export type NormalizedTrustStatus =
  "allowed" | "blocked" | "unknown" | "absent";

export interface NormalizedTrustGate {
  status: NormalizedTrustStatus;
  reason?: string;
  message?: string;
}

export interface NormalizedEligiblePlacement {
  placement: ResolvedPlacement;
  available: boolean;
  blockers: RunBlocker[];
  /**
   * Pre-launch usage estimate for this placement, as a PERCENTAGE RANGE of the
   * account's monthly cloud allowance (never raw credits). Optional and
   * mock-first: absent until the server returns it, in which case the UI shows
   * no estimate (quiet degrade). e.g. { min: 3, max: 5 } → "~3–5% of monthly".
   */
  estimatedUsagePercent?: { min: number; max: number };
}

/**
 * Public Preview Runner v0 offer (ato#1006, contract C1). A single, already-
 * decided run affordance computed server-side by precedence — the client never
 * re-derives it per-field. When present it DRIVES the run CTA (see GetRun); the
 * legacy placement / trust / estimatedMode rendering is skipped. Managed-cloud
 * substrate reasons never appear here — the preview lane ignores them entirely.
 */
export type RunOfferKind =
  | "run_now"
  | "preparing"
  | "busy"
  | "sign_in_required"
  | "run_locally"
  | "unsupported";

export interface RunOffer {
  /** The decided affordance (also the object's discriminant). */
  offer: RunOfferKind;
  /** Button label. Null only for "unsupported" (no run CTA). */
  cta: string | null;
  /** "free_preview" for the login-free disposable lane; null otherwise. */
  lane: "free_preview" | null;
  /** Preview lease hard cap in seconds (free_preview only); null otherwise. */
  limitSeconds: number | null;
  login_required: boolean;
  /** Optional human copy (e.g. the "busy" retry message). */
  message?: string;
}

export interface NormalizedRunPreview {
  previewId?: string;
  requestedPlacement?: string;
  selectedPlacement: ResolvedPlacement | null;
  eligiblePlacements: NormalizedEligiblePlacement[];
  blockers: RunBlocker[];
  trustGate: NormalizedTrustGate;
  requiredConsents: ConsentRequirement[];
  budgetGate: BudgetGate | null;
  estimatedMode: string | null;
  /**
   * Whether the managed-cloud run button may be offered. Conservatively false
   * unless the canonical contract confirms: selected placement === "cloud",
   * zero blockers, estimatedMode === "ready", and trust allowed/absent.
   */
  canRunManaged: boolean;
  /**
   * Public Preview Runner v0 offer (ato#1006, C1). Non-null when the server
   * returned a decided `offer`; the run CTA is then driven entirely by it.
   * Null on older servers (legacy placement/trust rendering applies).
   */
  offer: RunOffer | null;
}

/**
 * Result of POST /v1/preview-runs — the login-free free_preview lane launch
 * ACTION (ato#1006, C1/C7). On a minted lease the server returns the OWNER-LESS
 * run id + the runner's raw app URL + the lease cap; when it could not mint one
 * (busy / lost capacity / no longer eligible) it returns ONLY a fresh `offer`
 * (run_id null) so the CTA can reflect the new state instead of dead-clicking.
 * The lane never consults the managed-cloud substrate (C1 hard rule).
 */
export interface PreviewRunStart {
  /** Owner-less run id (runs.user_id = NULL); null when no lease was minted. */
  runId: string | null;
  /** Runner's raw public session route for the app; null unless a lease minted. */
  appUrl: string | null;
  /** GET status path for the anon run (e.g. "/v1/runs/:id"); null when absent. */
  statusUrl: string | null;
  /** Per-run control token (returned once); null when no run was created. */
  controlToken: string | null;
  /** Single-use guest app-view credential. Kept in memory and returned once. */
  appViewToken: string | null;
  /** Preview lease hard cap in seconds; null when absent. */
  limitSeconds: number | null;
  /** The server's decided offer; drives the busy/ineligible CTA when runId null. */
  offer: RunOffer | null;
  /**
   * A Static Web delivery (vertical slice): present only when the server
   * short-circuited to a CDN URL and created NO run/lease/token. When this is
   * set, `runId`, `controlToken`, `appViewToken`, `statusUrl`, and
   * `limitSeconds` are all null, and the client must NOT poll, stop, time out,
   * or mint continuation state.
   */
  delivery: StaticWebDelivery | null;
}

/** An active public Static Web delivery the API resolved (no workload). */
export interface StaticWebDelivery {
  kind: "static_web";
  appUrl: string;
  hostLabel: string;
  manifestDigest: string;
  materializationId: string;
  capsuleRevisionId: string;
  environment: "production";
  immutable: true;
}

/**
 * A live preview's server-authoritative continuation horizon (ADR-3).
 *
 * This is deliberately separate from `RunStatus`: an iframe can remain
 * `ready` while the continuation moves through `offered`, `external`, and
 * `applying`. `resumeMode` has one accepted value because this slice extends
 * the same live workload; it is never a stop/start or checkpoint restore.
 */
export type PreviewContinuationOption = "sign_in" | "cloud_subscription";

export type PreviewContinuationServerState =
  | "unavailable"
  | "armed"
  | "offered"
  | "external"
  | "applying"
  | "extended"
  | "expired";

export interface PreviewContinuation {
  available: boolean;
  resumeMode: "live_extend" | null;
  state: PreviewContinuationServerState;
  options: PreviewContinuationOption[];
  generation: number;
  appliedGeneration: number;
  expiresAt: string | null;
  offerAt: string | null;
  graceExpiresAt: string | null;
}

export interface PreviewContinuationMutation {
  action: "begin" | "apply";
  option: PreviewContinuationOption;
}

export interface RunUrl {
  kind?: string;
  url: string;
}

/**
 * Owner-facing pending ExecutionPlan consent (P4-A). Present only when a
 * Connected Runner gated a dispatched run at E302 and is waiting for the owner
 * to approve THIS exact policy. The decision contract is the full 5-tuple
 * (scoped_id, version, target_label, policy_segment_hash, provisioning_policy_hash);
 * `consent_ref` is its binding/display hash. The approval POST echoes the exact
 * `consent_ref` back so the API can bind the decision to the gated policy.
 */
export interface PendingConsent {
  consent_ref: string;
  status: string;
  scoped_id: string;
  version: string;
  target_label: string;
  policy_segment_hash: string;
  provisioning_policy_hash: string;
  /** Human policy summary; may be empty, never absent. */
  summary: string;
  requested_at: string | null;
  expires_at: string | null;
}

/** Normalized run (POST /v1/runs, GET /v1/runs/:id). */
export interface Run {
  id: string;
  status: RunStatus;
  /** Canonical "publisher/slug" join key (see RunSummary.capsule_scoped_id). */
  capsule_scoped_id?: string | null;
  placement?: ResolvedPlacement | null;
  /** Cloud "open app" URL when the run is ready. */
  user_visible_url?: string | null;
  /** Local launch handoff — a capsule:// URL or desktop deep link. */
  launch_url?: string | null;
  /** Canonical URL list: [{ kind, url }]. */
  urls: RunUrl[];
  /**
   * External URL reported by a Connected Runner — present only after the
   * runner's local proxy is actually serving. For runner runs "Open app"
   * must use EXACTLY this value (no synthesis, no fallback scavenging).
   */
  ready_url?: string | null;
  /**
   * Authenticated, user-facing app proxy URL (`<slug>.app.ato.run`) minted for
   * a ready web session. Prefer this over `ready_url` when opening: it puts Ato
   * sign-in + ownership in front, hides the raw runner origin, and is publicly
   * resolvable (Cloudflare Workers) so the browser doesn't need to reach the
   * runner's tailnet/public IP directly. Null until the proxy mints one.
   */
  app_url?: string | null;
  /** Canonical live presentation contract when the run is ready. */
  surface?: SessionSurface | null;
  /** Present when the run could not be created at the requested placement. */
  blockers?: RunBlocker[];
  /** Human-readable failure reason when status === "failed". */
  error?: string | null;
  estimatedMode?: string | null;
  /**
   * Pending ExecutionPlan consent (P4-A) — present only when status is
   * "needs_consent" AND the server returned a COMPLETE 5-tuple payload. Null on
   * anything missing/malformed so the approval modal never renders partial
   * policy (the modal is shown iff this is non-null).
   */
  pending_consent?: PendingConsent | null;
  /** Server-derived Invite modes for this exact live Run/current point. */
  collaboration?: RunCollaborationCapabilities;
}

export interface CollaborationCapability {
  supported: boolean;
  reason?: string;
}

export interface RunCollaborationCapabilities {
  separate: CollaborationCapability;
  together: CollaborationCapability;
}

/** One row of GET /v1/runs — the Activity / run-list view. The server decides
 *  active vs recent membership (we never reclassify status client-side). */
export interface RunSummary {
  id: string;
  label: string;
  /** Canonical "publisher/slug" of the capsule this run executes — the ONLY
   *  key for joining runs to Store/library apps. `label` is display-only and
   *  falls back to the run id for app_id/preview_id-created runs. */
  capsule_scoped_id: string | null;
  status: RunStatus;
  placement: string | null;
  runner_id: string | null;
  runner_display_name: string | null;
  /** Connected-Runner public URL; present only when its proxy is serving. */
  ready_url: string | null;
  /** Authenticated app proxy URL; prefer over ready_url. Null until minted. */
  app_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  stopped_at: string | null;
  error: string | null;
}

/**
 * Account monthly cloud usage, ALWAYS a PERCENTAGE of the plan's monthly
 * allowance — the PWA never shows raw credits (internal ledger stays internal;
 * the pricing decision is external-% / internal-credits). Mock-first: the
 * GET /v1/account/cloud-usage endpoint may not exist yet, in which case the
 * client returns null and the UI renders nothing (quiet degrade).
 */
export type CloudUsageLimitState = "ok" | "near_limit" | "at_limit";

export interface CloudUsage {
  /** 0–100, clamped. The single number the meter renders. */
  usedPercent: number;
  /** ISO date the monthly allowance resets (optional). */
  renewsAt?: string | null;
  /** Plan label for context (e.g. "Pro"); never a credit count. */
  planLabel?: string | null;
  /** Server-derived bucket so the UI need not hardcode thresholds. */
  limitState?: CloudUsageLimitState;
  /** True at/over the monthly allowance (gates the limit messaging). */
  atLimit?: boolean;
}

/**
 * Normalize a raw GET /v1/account/cloud-usage body. The live ato-api contract is
 * `{ percentUsed, resetAt, limitState }`; older snake_case keys are still
 * accepted so mocks/fixtures keep working. The percentage is clamped to 0–100;
 * anything unparseable yields null (UI then shows no meter). Never reads or
 * exposes a raw credit field even if the server were to include one.
 */
export function normalizeCloudUsage(raw: unknown): CloudUsage | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const num = (...keys: string[]): number | null => {
    for (const k of keys) if (typeof r[k] === "number") return r[k] as number;
    return null;
  };
  const str = (...keys: string[]): string | null => {
    for (const k of keys) if (typeof r[k] === "string") return r[k] as string;
    return null;
  };
  const pctRaw = num("percentUsed", "used_percent", "usedPercent");
  if (pctRaw == null || Number.isNaN(pctRaw)) return null;
  const usedPercent = Math.max(0, Math.min(100, pctRaw));
  const rawState = r["limitState"];
  const limitState: CloudUsageLimitState | undefined =
    rawState === "ok" || rawState === "near_limit" || rawState === "at_limit"
      ? rawState
      : undefined;
  return {
    usedPercent,
    renewsAt: str("resetAt", "renews_at", "renewsAt"),
    planLabel: str("plan_label", "planLabel"),
    limitState,
    atLimit:
      limitState === "at_limit" ||
      r["at_limit"] === true ||
      r["atLimit"] === true ||
      usedPercent >= 100,
  };
}

// ── Low-level coercion helpers ────────────────────────────────────────────────

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

const CONTINUATION_STATES: ReadonlySet<string> = new Set([
  "unavailable",
  "armed",
  "offered",
  "external",
  "applying",
  "extended",
  "expired",
]);

const CONTINUATION_OPTIONS: ReadonlySet<string> = new Set([
  "sign_in",
  "cloud_subscription",
]);

function unavailableContinuation(
  state: "unavailable" | "expired" = "unavailable",
): PreviewContinuation {
  return {
    available: false,
    resumeMode: null,
    state,
    options: [],
    generation: 0,
    appliedGeneration: 0,
    expiresAt: null,
    offerAt: null,
    graceExpiresAt: null,
  };
}

function nullableIsoDate(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (
    typeof v !== "string" ||
    v.length === 0 ||
    !Number.isFinite(Date.parse(v))
  ) {
    return undefined;
  }
  return v;
}

function nonNegativeInteger(v: unknown): number | undefined {
  return typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : undefined;
}

/**
 * Normalize the ADR-3 continuation DTO. The canonical wire format is
 * snake_case at the top level; a short-lived `{ continuation: ... }` envelope
 * and camelCase field aliases are accepted to keep staged API rollouts
 * backwards compatible.
 *
 * The boundary fails closed. Any unknown state/option, malformed generation,
 * wrong resume mode, or missing deadline turns the result into `unavailable`
 * so the browser never presents an action it cannot safely complete.
 */
export function normalizePreviewContinuation(
  raw: unknown,
): PreviewContinuation {
  const root = asRecord(raw);
  const nested = asRecord(root?.continuation);
  const r = nested ?? root;
  if (!r) return unavailableContinuation();

  const stateValue = r.state;
  const state =
    typeof stateValue === "string" && CONTINUATION_STATES.has(stateValue)
      ? (stateValue as PreviewContinuationServerState)
      : null;

  const generation = nonNegativeInteger(r.generation);
  const appliedGeneration = nonNegativeInteger(
    r.applied_generation ?? r.appliedGeneration,
  );
  const expiresAt = nullableIsoDate(r.expires_at ?? r.expiresAt);

  // An unavailable/expired response has no actionable fields. Preserve the
  // terminal distinction only when it carries a complete horizon. Keeping its
  // generation lets the hook ignore an expired response that lost a race with
  // a newer accepted extension; a partial terminal response fails closed to
  // non-actionable `unavailable` instead of tearing down a live iframe.
  if (r.available !== true) {
    if (
      state === "expired" &&
      generation !== undefined &&
      appliedGeneration !== undefined &&
      appliedGeneration <= generation &&
      expiresAt != null
    ) {
      return {
        available: false,
        resumeMode: null,
        state: "expired",
        options: [],
        generation,
        appliedGeneration,
        expiresAt,
        offerAt: null,
        graceExpiresAt: null,
      };
    }
    return unavailableContinuation();
  }

  const resumeMode = r.resume_mode ?? r.resumeMode;
  const offerAt = nullableIsoDate(r.offer_at ?? r.offerAt);
  const graceExpiresAt = nullableIsoDate(
    r.grace_expires_at ?? r.graceExpiresAt,
  );
  const rawOptions = r.options;
  const generationStateIsValid =
    generation !== undefined &&
    appliedGeneration !== undefined &&
    (state === "applying"
      ? appliedGeneration < generation
      : appliedGeneration === generation);

  if (
    resumeMode !== "live_extend" ||
    !state ||
    state === "unavailable" ||
    generation === undefined ||
    appliedGeneration === undefined ||
    !generationStateIsValid ||
    expiresAt == null ||
    !Array.isArray(rawOptions) ||
    rawOptions.some(
      (option) =>
        typeof option !== "string" || !CONTINUATION_OPTIONS.has(option),
    )
  ) {
    return unavailableContinuation();
  }

  // Armed/offered need an offer deadline; an external/applying transaction
  // needs its bounded grace deadline. Missing either is not safe to infer.
  if ((state === "armed" || state === "offered") && offerAt == null) {
    return unavailableContinuation();
  }
  // `applying` can legitimately expose the previous applied deadline with no
  // grace deadline while the runner is still acknowledging the newer horizon.
  // Once the begin generation is applied, the API moves to `external` and the
  // bounded grace deadline becomes mandatory.
  if (state === "external" && graceExpiresAt == null) {
    return unavailableContinuation();
  }

  return {
    available: true,
    resumeMode: "live_extend",
    state,
    options: rawOptions as PreviewContinuationOption[],
    generation,
    appliedGeneration,
    expiresAt,
    offerAt: offerAt ?? null,
    graceExpiresAt: graceExpiresAt ?? null,
  };
}

function stringOr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function normalizePlacement(v: unknown): ResolvedPlacement | null {
  return v === "cloud" || v === "local" || v === "desktop" ? v : null;
}

function normalizeBlocker(entry: unknown): RunBlocker | null {
  // Canonical contract: blockers are plain strings.
  if (typeof entry === "string") return entry ? { code: entry } : null;
  // Legacy / transitional: { code, message }.
  const r = asRecord(entry);
  const code = stringOr(r?.code);
  if (!code) return null;
  return { code, message: stringOr(r?.message) };
}

function normalizeBlockers(v: unknown): RunBlocker[] {
  if (!Array.isArray(v)) return [];
  return v.map(normalizeBlocker).filter((b): b is RunBlocker => b !== null);
}

function normalizeTrustGate(v: unknown): NormalizedTrustGate {
  const r = asRecord(v);
  if (!r) return { status: "absent" };

  const message = stringOr(r.message);

  // Canonical contract: { status: "allowed" | "blocked" | "unknown", reason? }.
  const status = stringOr(r.status);
  const reason = stringOr(r.reason);
  if (status === "allowed") return { status: "allowed", reason, message };
  if (status === "blocked") return { status: "blocked", reason, message };
  if (status === "unknown") {
    return { status: "unknown", reason: reason ?? "not_evaluated", message };
  }

  // Legacy: { state, message }.
  const state = stringOr(r.state);
  if (state) {
    if (state === "approved" || state === "allowed" || state === "accepted") {
      return { status: "allowed", reason: state, message };
    }
    if (state === "unknown" || state === "not_evaluated") {
      return { status: "unknown", reason: state, message };
    }
    // requires_review[_for_cloud] / blocked / rejected / cloud_execution_not_allowed
    return { status: "blocked", reason: state, message };
  }

  // Object present but unrecognized → unknown (honest, never "allowed").
  return { status: "unknown", reason: "not_evaluated", message };
}

function normalizeConsents(v: unknown): ConsentRequirement[] {
  if (!Array.isArray(v)) return [];
  return v.map((entry) => {
    const r = asRecord(entry);
    return { id: stringOr(r?.id), label: stringOr(r?.label) };
  });
}

function normalizeBudgetGate(v: unknown): BudgetGate | null {
  const r = asRecord(v);
  if (!r) return null;
  return {
    state: stringOr(r.state) ?? stringOr(r.status),
    required_cents:
      typeof r.required_cents === "number" ? r.required_cents : undefined,
    currency: stringOr(r.currency),
    message: stringOr(r.message),
  };
}

/** Parse a { min, max } percentage range (camel or snake), clamped & ordered.
 *  Returns undefined when absent/invalid so the UI shows no estimate. */
function parseUsageRange(v: unknown): { min: number; max: number } | undefined {
  const r = asRecord(v);
  if (!r) return undefined;
  const min = typeof r["min"] === "number" ? (r["min"] as number) : undefined;
  const max = typeof r["max"] === "number" ? (r["max"] as number) : undefined;
  if (min == null && max == null) return undefined;
  const lo = Math.max(0, min ?? max ?? 0);
  const hi = Math.max(lo, max ?? min ?? 0);
  return { min: lo, max: hi };
}

const RUN_OFFER_KINDS: ReadonlySet<string> = new Set([
  "run_now",
  "preparing",
  "busy",
  "sign_in_required",
  "run_locally",
  "unsupported",
]);

/**
 * Normalize a Public Preview Runner v0 `offer` object (ato#1006, C1). Returns
 * null when absent or the kind is unrecognized, so older servers fall back to
 * the legacy placement/trust rendering. Accepts `limit_seconds` (canonical) or
 * `limitSeconds`. The server has already decided the offer by precedence — this
 * only coerces types, it never re-derives the kind.
 */
function normalizeOffer(v: unknown): RunOffer | null {
  const r = asRecord(v);
  if (!r) return null;
  const kind = stringOr(r.offer);
  if (!kind || !RUN_OFFER_KINDS.has(kind)) return null;
  const limit =
    typeof r.limit_seconds === "number"
      ? (r.limit_seconds as number)
      : typeof r.limitSeconds === "number"
        ? (r.limitSeconds as number)
        : null;
  return {
    offer: kind as RunOfferKind,
    cta: stringOr(r.cta) ?? null,
    lane: r.lane === "free_preview" ? "free_preview" : null,
    limitSeconds: limit,
    login_required: r.login_required === true,
    message: stringOr(r.message),
  };
}

function normalizeEligiblePlacements(
  v: unknown,
): NormalizedEligiblePlacement[] {
  if (!Array.isArray(v)) return [];
  const out: NormalizedEligiblePlacement[] = [];
  for (const entry of v) {
    // Canonical: { placement, available, blockers, estimatedUsagePercent? }.
    const r = asRecord(entry);
    if (r) {
      const placement = normalizePlacement(r.placement);
      if (!placement) continue;
      const est = parseUsageRange(
        r["estimatedUsagePercent"] ?? r["estimated_usage_percent"],
      );
      out.push({
        placement,
        available: r.available === true,
        blockers: normalizeBlockers(r.blockers),
        ...(est ? { estimatedUsagePercent: est } : {}),
      });
      continue;
    }
    // Legacy: bare placement strings.
    const placement = normalizePlacement(entry);
    if (placement) out.push({ placement, available: true, blockers: [] });
  }
  return out;
}

// ── Public normalizers ────────────────────────────────────────────────────────

/**
 * Normalize a POST /v1/runs/preview response. Prefers `raw.contract`, falling
 * back per-field to the top-level for older servers. Never treats a missing
 * contract / placement / estimatedMode as runnable.
 */
export function normalizePreviewResponse(raw: unknown): NormalizedRunPreview {
  const root = asRecord(raw) ?? {};
  const contract = asRecord(root.contract);

  // Per-field precedence: canonical `contract` first, then top-level.
  const pick = (key: string): unknown =>
    contract && key in contract ? contract[key] : root[key];

  const selectedPlacement = normalizePlacement(pick("selectedPlacement"));
  const blockers = normalizeBlockers(pick("blockers"));
  const trustGate = normalizeTrustGate(pick("trustGate"));
  const estimatedMode = stringOr(pick("estimatedMode")) ?? null;
  const requiredConsents = normalizeConsents(pick("requiredConsents"));
  const budgetGate = normalizeBudgetGate(pick("budgetGate"));
  const eligiblePlacements = normalizeEligiblePlacements(
    pick("eligiblePlacements"),
  );
  const previewId =
    stringOr(pick("previewId")) ??
    stringOr(root.preview_id) ??
    stringOr(root.previewId);
  const requestedPlacement = stringOr(pick("requestedPlacement"));

  const trustOk =
    trustGate.status === "allowed" || trustGate.status === "absent";
  const canRunManaged =
    selectedPlacement === "cloud" &&
    blockers.length === 0 &&
    estimatedMode === "ready" &&
    trustOk;

  // The `offer` is a top-level field (C1) but tolerate a nested contract too.
  const offer = normalizeOffer(pick("offer"));

  return {
    previewId,
    requestedPlacement,
    selectedPlacement,
    eligiblePlacements,
    blockers,
    trustGate,
    requiredConsents,
    budgetGate,
    estimatedMode,
    canRunManaged,
    offer,
  };
}

/**
 * Normalize a POST /v1/preview-runs response (ato#1006, C1/C7). Both the 201
 * "launched" body ({ run_id, control_token, status_url, app_url, limit_seconds,
 * offer }) and the 200 "no lease" body ({ offer }) map here — `runId` is the
 * discriminant (non-null ⇒ a lease was minted). Type-coercion only; the server
 * has already decided the offer.
 */
export function normalizePreviewRunStart(raw: unknown): PreviewRunStart {
  const r = asRecord(raw) ?? {};
  const delivery = normalizeStaticWebDelivery(r.delivery);
  return {
    runId: delivery ? null : (stringOr(r.run_id) ?? null),
    appUrl: delivery ? null : (stringOr(r.app_url) ?? null),
    statusUrl: delivery ? null : (stringOr(r.status_url) ?? null),
    controlToken: delivery ? null : (stringOr(r.control_token) ?? null),
    appViewToken: delivery ? null : (stringOr(r.app_view_token) ?? null),
    limitSeconds:
      delivery || typeof r.limit_seconds !== "number"
        ? null
        : (r.limit_seconds as number),
    offer: delivery ? null : normalizeOffer(r.offer),
    delivery,
  };
}

/**
 * Parse a Static Web delivery object. A delivery is authoritative: when the
 * server returned one, the run is a CDN URL and NOTHING else on the response is
 * meaningful. Malformed deliveries fail closed to null (the legacy path).
 */
export function normalizeStaticWebDelivery(
  raw: unknown,
): StaticWebDelivery | null {
  const r = asRecord(raw);
  if (!r || r.kind !== "static_web") return null;
  const appUrl = stringOr(r.app_url);
  const hostLabel = stringOr(r.host_label);
  const manifestDigest = stringOr(r.manifest_digest);
  const materializationId = stringOr(r.materialization_id);
  const capsuleRevisionId = stringOr(r.capsule_revision_id);
  if (
    !appUrl ||
    !hostLabel ||
    !manifestDigest ||
    !materializationId ||
    !capsuleRevisionId ||
    r.environment !== "production" ||
    r.immutable !== true
  ) {
    return null;
  }
  return {
    kind: "static_web",
    appUrl,
    hostLabel,
    manifestDigest,
    materializationId,
    capsuleRevisionId,
    environment: "production",
    immutable: true,
  };
}

const LOCAL_URL_KINDS = new Set([
  "local",
  "deep-link",
  "deeplink",
  "desktop",
  "launch",
]);
const CLOUD_URL_KINDS = new Set([
  "public",
  "cloud",
  "web",
  "user-visible",
  "user_visible",
]);

function isDeepLinkUrl(url: string): boolean {
  // Custom-scheme handoffs (e.g. capsule://…) are local launch links, never
  // an http(s) "open in browser" URL.
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !/^https?:\/\//i.test(url);
}

function pickUrlByKind(urls: RunUrl[], kinds: Set<string>): string | undefined {
  return urls.find((u) => u.kind != null && kinds.has(u.kind))?.url;
}

function normalizeUrls(v: unknown): RunUrl[] {
  if (!Array.isArray(v)) return [];
  const out: RunUrl[] = [];
  for (const entry of v) {
    const r = asRecord(entry);
    const url = stringOr(r?.url);
    if (!url) continue;
    out.push({ kind: stringOr(r?.kind), url });
  }
  return out;
}

/**
 * Normalize a POST /v1/runs or GET /v1/runs/:id response. Accepts both the
 * canonical `{ run_id, urls: [{kind,url}] }` shape and the transitional
 * top-level `{ id, launch_url, user_visible_url }` shape.
 */
/**
 * Validate + normalize the run DTO's `pending_consent`. Returns null on ANY
 * missing/malformed field so the approval modal never renders partial or forged
 * policy: the full 5-tuple must be present, identity fields non-empty, the
 * consent_ref + policy hashes blake3-prefixed, and `summary` present (an empty
 * string is allowed — it is read directly, since stringOr() rejects "").
 */
export function normalizePendingConsent(raw: unknown): PendingConsent | null {
  const r = asRecord(raw);
  if (!r) return null;
  const consent_ref = stringOr(r.consent_ref);
  const scoped_id = stringOr(r.scoped_id);
  const version = stringOr(r.version);
  const target_label = stringOr(r.target_label);
  const policy_segment_hash = stringOr(r.policy_segment_hash);
  const provisioning_policy_hash = stringOr(r.provisioning_policy_hash);
  if (!consent_ref || !consent_ref.startsWith("blake3:")) return null;
  if (!scoped_id || !version || !target_label) return null;
  if (!policy_segment_hash || !policy_segment_hash.startsWith("blake3:"))
    return null;
  if (
    !provisioning_policy_hash ||
    !provisioning_policy_hash.startsWith("blake3:")
  )
    return null;
  // summary must be PRESENT (may be ""); read directly because stringOr drops "".
  if (typeof r.summary !== "string") return null;
  return {
    consent_ref,
    status: stringOr(r.status) ?? "pending",
    scoped_id,
    version,
    target_label,
    policy_segment_hash,
    provisioning_policy_hash,
    summary: r.summary,
    requested_at: stringOr(r.requested_at) ?? null,
    expires_at: stringOr(r.expires_at) ?? null,
  };
}

export function normalizeRunResponse(raw: unknown): Run {
  const r = asRecord(raw) ?? {};
  const id = stringOr(r.id) ?? stringOr(r.run_id) ?? "";
  const status = (stringOr(r.status) ?? "queued") as RunStatus;
  const placement = normalizePlacement(r.placement);
  const urls = normalizeUrls(r.urls);

  // local / deep-link URL → open as launch_url; public / cloud URL → open as
  // user_visible_url. Explicit top-level fields win; otherwise derive from urls.
  const launch_url =
    stringOr(r.launch_url) ??
    pickUrlByKind(urls, LOCAL_URL_KINDS) ??
    urls.find((u) => isDeepLinkUrl(u.url))?.url ??
    null;
  const user_visible_url =
    stringOr(r.user_visible_url) ??
    pickUrlByKind(urls, CLOUD_URL_KINDS) ??
    urls.find((u) => /^https?:\/\//i.test(u.url))?.url ??
    null;

  const ready_url =
    stringOr(r.ready_url) ?? urls.find((u) => u.kind === "ready")?.url ?? null;
  const app_url =
    stringOr(r.app_url) ?? urls.find((u) => u.kind === "app")?.url ?? null;
  const surface = sessionSurfaceFromEnvelope(r);

  return {
    id,
    status,
    capsule_scoped_id: stringOr(r.capsule_scoped_id) ?? null,
    placement,
    launch_url,
    user_visible_url,
    ready_url,
    app_url,
    surface,
    urls,
    blockers: normalizeBlockers(r.blockers),
    error: stringOr(r.error) ?? null,
    estimatedMode: stringOr(r.estimatedMode) ?? null,
    pending_consent: normalizePendingConsent(r.pending_consent),
    collaboration: normalizeCollaboration(r.collaboration),
  };
}

function normalizeCollaboration(
  value: unknown,
): RunCollaborationCapabilities | undefined {
  const record = asRecord(value);
  const separate = asRecord(record?.separate);
  const together = asRecord(record?.together);
  if (
    typeof separate?.supported !== "boolean" ||
    typeof together?.supported !== "boolean"
  ) {
    return undefined;
  }
  return {
    separate: {
      supported: separate.supported,
      ...(stringOr(separate.reason)
        ? { reason: stringOr(separate.reason)! }
        : {}),
    },
    together: {
      supported: together.supported,
      ...(stringOr(together.reason)
        ? { reason: stringOr(together.reason)! }
        : {}),
    },
  };
}

function normalizeRunSummary(raw: unknown): RunSummary | null {
  const r = asRecord(raw);
  const id = stringOr(r?.id);
  if (!id) return null;
  return {
    id,
    label: stringOr(r?.label) ?? id,
    capsule_scoped_id: stringOr(r?.capsule_scoped_id) ?? null,
    status: (stringOr(r?.status) ?? "queued") as RunStatus,
    placement: stringOr(r?.placement) ?? null,
    runner_id: stringOr(r?.runner_id) ?? null,
    runner_display_name: stringOr(r?.runner_display_name) ?? null,
    ready_url: stringOr(r?.ready_url) ?? null,
    app_url: stringOr(r?.app_url) ?? null,
    created_at: stringOr(r?.created_at) ?? null,
    updated_at: stringOr(r?.updated_at) ?? null,
    stopped_at: stringOr(r?.stopped_at) ?? null,
    error: stringOr(r?.error) ?? null,
  };
}

/** Normalize the `{ runs: [...] }` envelope from GET /v1/runs. */
export function normalizeRunList(raw: unknown): RunSummary[] {
  const r = asRecord(raw);
  const list = Array.isArray(r?.runs) ? r.runs : [];
  return list
    .map(normalizeRunSummary)
    .filter((x): x is RunSummary => x !== null);
}

/**
 * Best URL to open for a run. Checks, in order:
 *   1. launch_url (local / deep-link handoff)
 *   2. user_visible_url (cloud open-app URL)
 *   3. first usable urls[] entry
 *
 * LEGACY: only GetRun's reload-by-run_id tracking view still needs this
 * (non-runner run shapes). Launch-driven surfaces get their view URL from the
 * launch payload (openCapsuleClient) or use runnerOpenUrl for run summaries —
 * don't add new callers.
 */
export function runOpenUrl(run: Run): string | null {
  if (run.launch_url) return run.launch_url;
  if (run.user_visible_url) return run.user_visible_url;
  const first = run.urls?.find(
    (u) => typeof u.url === "string" && u.url.length > 0,
  );
  return first?.url ?? null;
}

/**
 * The URL to open for a runner-served web session: prefer the authenticated
 * `app_url` (Ato sign-in + ownership in front, raw runner origin hidden), and
 * fall back to the raw `ready_url` only where no app_url was minted (non-prod /
 * pre-migration) so existing flows don't regress. Returns null when neither is
 * available (e.g. not ready yet). Accepts both Run and RunSummary.
 */
export function runnerOpenUrl(run: {
  app_url?: string | null;
  ready_url?: string | null;
}): string | null {
  return run.app_url ?? run.ready_url ?? null;
}
